import React, { useState } from "react";
import "./Filtering.scss";
import { useTranslation } from "react-i18next";
import { prettifyString } from "../strings";
import { faChevronDown, faChevronUp, faMultiply, faCircleXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "../../dropdown/Dropdown";
import { IDropdownItem } from "../../dropdown/dropdownUtils";
import Button from "../../button/Button";
import Header from "../../layout/header/Header";
import HeaderContainer from "../../layout/header/HeaderContainers/HeaderContainer";

interface IProps {
    show: boolean;
    filtering: { [key: string]: { [key: string]: boolean } };
    onFilteringChnage: (filtering: { [key: string]: { [key: string]: boolean } }) => void;
    onClearAllFiltering: () => void;
}

interface ILabelValue {
    label: string;
    value: any;
}

export default function Filtering(props: IProps) {
    const { t } = useTranslation();

    const clearAllFiltering = (e?: any): void => {
        props.onClearAllFiltering();
    };

    const countTrue = (array: ILabelValue[]): number => {
        let count = 0;
        for (let i = 0; i < array.length; i++) {
            if (array[i].value) count++;
        }
        return count;
    };

    const firstOneYes = (array: ILabelValue[]): ILabelValue => {
        for (let i = 0; i < array.length; i++) {
            if (array[i].value) return array[i];
        }
        return { label: "N/A", value: false };
    };

    const FilteringDropdown = ({ element }: { element: ILabelValue }) => {
        const [isOpen, setIsOpen] = useState(false);

        const filteringElements: ILabelValue[] = [];

        for (const property in element.value) {
            filteringElements.push({ label: property, value: element.value[property] });
        }

        const numberSelected = countTrue(filteringElements);

        // Word:
        let word = "N/A";
        let number = 0;
        if (filteringElements.length === 1) {
            if (numberSelected === 1) word = t("Decisions.Filtering.yes");
            else word = t("Decisions.Filtering.no");
        } else {
            if (numberSelected === 0) word = t("Decisions.Filtering.all");
            else if (filteringElements.length === numberSelected) {
                word = "All";
            } else {
                word = prettifyString(firstOneYes(filteringElements).label);
                number = numberSelected - 1;
            }
        }

        const items: IDropdownItem[] = filteringElements.map((entry, index) => ({
            id: `${element.label}__${entry.label}`,
            title: prettifyString(entry.label),
            options: "checkbox",
            value: entry.value ? "selected" : "unselected",
            doNotCloseOnClick: true,
            divider: index + 1 === filteringElements.length && filteringElements.length > 1,
        }));

        if (filteringElements.length > 1) {
            items.push({
                id: `${element.label}__clear-all`,
                title: t("Filtering.clearFilters"),
                icon: <FontAwesomeIcon icon={faMultiply} />,
                doNotCloseOnClick: false,
            });
        }

        const handleChange = (item: IDropdownItem) => {
            if (item.id === `${element.label}__clear-all`) {
                clearAllFiltering();
                return;
            }

            const separator = `${element.label}__`;
            const propertyLabel = item.id.startsWith(separator) ? item.id.slice(separator.length) : item.id;

            props.onFilteringChnage({
                ...props.filtering,
                [element.label]: { ...element.value, [propertyLabel]: !element.value[propertyLabel] },
            });
        };

        return (
            <Dropdown
                key={element.label}
                items={items}
                maxHeight={560}
                onOpenChange={setIsOpen}
                onChange={handleChange}
                trigger={
                    <div className="uDropdown">
                        <div className="uDropdownText">{prettifyString(element.label)}</div>
                        <div className="uDropdownInfoWrapper">
                            <div className="uDropdownInfoText">{word}</div>
                            {number >= 1 && (
                                <div className="uDropdownInfoNumber">
                                    <div className="numberInside">{"+" + number}</div>
                                </div>
                            )}
                        </div>
                        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                    </div>
                }
            />
        );
    };

    return (
        <>
            {props.show ? (
                <Header>
                    <HeaderContainer>
                        {Object.entries(props.filtering).length > 0 &&
                            Object.entries(props.filtering).map((key) => (
                                <FilteringDropdown key={key[0]} element={{ label: key[0], value: key[1] }} />
                            ))}
                        {Object.entries(props.filtering).length === 0 && (
                            <div className="uFilteringMessage">
                                <FontAwesomeIcon icon={faCircleXmark} />
                                <div className="uFilteringMessageText">{t("Decisions.Filtering.noLayouts")}</div>
                            </div>
                        )}
                    </HeaderContainer>
                    <HeaderContainer>
                        <Button variant="secondary" onClick={clearAllFiltering}>
                            <FontAwesomeIcon icon={faMultiply} />
                            {t("Decisions.Filtering.clearFilters")}
                        </Button>
                    </HeaderContainer>
                </Header>
            ) : null}
        </>
    );
}
